/**
 * Toast Store - Global toast notification management
 * 
 * Provides a global toast notification system that can be used anywhere in the app.
 */

import { useMemo } from 'react';
import { create } from 'zustand';

// ============================================================================
// Types
// ============================================================================

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  title?: string;
  message: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastState {
  toasts: Toast[];
  
  // Actions
  addToast: (toast: Omit<Toast, 'id'>) => string;
  removeToast: (id: string) => void;
  clearToasts: () => void;
  
  // Convenience methods
  success: (message: string, title?: string) => string;
  error: (message: string, title?: string) => string;
  warning: (message: string, title?: string) => string;
  info: (message: string, title?: string) => string;
}

// ============================================================================
// Store Implementation
// ============================================================================

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],

  addToast: (toast) => {
    const id = Math.random().toString(36).substring(2) + Date.now().toString(36);
    const duration = toast.duration ?? 5000;
    
    set((state) => ({
      toasts: [...state.toasts, { ...toast, id }],
    }));
    
    // Auto-remove after duration
    if (duration > 0) {
      setTimeout(() => {
        get().removeToast(id);
      }, duration);
    }
    
    return id;
  },

  removeToast: (id) => {
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }));
  },

  clearToasts: () => {
    set({ toasts: [] });
  },

  // Convenience methods
  success: (message, title) => {
    return get().addToast({ type: 'success', message, title });
  },

  error: (message, title) => {
    return get().addToast({ type: 'error', message, title, duration: 7000 });
  },

  warning: (message, title) => {
    return get().addToast({ type: 'warning', message, title });
  },

  info: (message, title) => {
    return get().addToast({ type: 'info', message, title });
  },
}));

// ============================================================================
// Hook for easy access
// ============================================================================

/**
 * Hook to get toast actions without subscribing to state changes
 * Uses individual selectors to get stable function references
 */
export function useToast() {
  const addToast = useToastStore((state) => state.addToast);
  const removeToast = useToastStore((state) => state.removeToast);
  const success = useToastStore((state) => state.success);
  const error = useToastStore((state) => state.error);
  const warning = useToastStore((state) => state.warning);
  const info = useToastStore((state) => state.info);
  
  // Return a stable object - Zustand selectors already return stable references
  // but we need to memoize the wrapper object to prevent re-renders
  return useMemo(() => ({ 
    addToast, removeToast, success, error, warning, info 
  }), [addToast, removeToast, success, error, warning, info]);
}
